import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TrialSignupModal from "../components/TrialSignupModal";
import { Button } from "../components/ui/button";
import { trackPageView, trackCTAClick } from "../utils/analytics";
import { 
  ArrowRight, Shield, TrendingUp, AlertTriangle, CheckCircle,
  BarChart3, Zap, Lock, Users, Target, Eye, FileCheck,
  ShoppingBag, DollarSign, Percent, Clock
} from "lucide-react";

const HomePage = () => {
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);

  useEffect(() => {
    trackPageView('/home');
  }, []);

  const benefits = [
    {
      icon: Shield,
      title: "Know What's Safe",
      description: "See exactly which price changes won't hurt your business before you make them."
    },
    {
      icon: AlertTriangle,
      title: "Spot the Risks",
      description: "Get warned about pricing moves that could cost you customers or margins."
    },
    {
      icon: TrendingUp,
      title: "Grow with Confidence",
      description: "Make pricing decisions backed by data, not guesswork."
    }
  ];

  const features = [
    {
      icon: Eye,
      title: "Price Elasticity Analysis",
      description: "Understand how sensitive your customers are to price changes for each product."
    },
    {
      icon: Target,
      title: "Safe Range Recommendations",
      description: "Know the exact price range where you can adjust without losing customers."
    },
    {
      icon: BarChart3,
      title: "Risk Score Dashboard",
      description: "Visual overview of your entire catalog's pricing health at a glance."
    },
    {
      icon: FileCheck,
      title: "What-If Scenarios",
      description: "Test price changes virtually before implementing them in the real world."
    }
  ];

  const stats = [
    { value: "3 min", label: "To first insights" },
    { value: "50+", label: "Products analyzed free" },
    { value: "Zero", label: "Sales calls required" }
  ];

  const useCases = [
    {
      icon: ShoppingBag,
      title: "Retail & E-commerce",
      problem: "Competitors dropped prices. Should you match?",
      solution: "See if matching will hurt margins or if you have room to compete."
    },
    {
      icon: DollarSign,
      title: "Wholesale & Distribution",
      problem: "Supplier costs went up. How much can you pass on?",
      solution: "Know the safe markup range for each customer segment."
    },
    {
      icon: Percent,
      title: "Seasonal Businesses",
      problem: "Planning a sale. How deep should discounts go?",
      solution: "Find the discount sweet spot that drives volume without killing profit."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header onStartTrial={() => setIsTrialModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-4 py-2 mb-8">
            <Zap className="w-4 h-4 text-[#2563EB]" />
            <span className="text-sm text-[#2563EB] font-medium">An AxioSera Product</span>
            <span className="text-[#94A3B8]">|</span>
            <span className="text-sm text-[#F8FAFC]">Pricing Confidence for SMBs</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-[#F8FAFC] mb-6 leading-tight">
            Pricing Confidence
            <br />
            <span className="text-[#2563EB]">Built for SMBs</span>
          </h1>
          
          <p className="text-xl text-[#94A3B8] mb-10 max-w-2xl mx-auto">
            No more guesswork. No more spreadsheets. PriceStrike shows you which 
            price changes are safe — and warns you about the ones that aren't.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/pricing-check" onClick={() => trackCTAClick('Run Free Pricing Check - Hero', '/home')}>
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg shadow-lg shadow-[#2563EB]/25">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <p className="text-[#94A3B8] text-sm">
            ✓ No credit card required &nbsp; ✓ No sales call &nbsp; ✓ Results in minutes
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 px-6 bg-[#111827] border-y border-[#2563EB]/20">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-[#2563EB]">{stat.value}</p>
                <p className="text-sm text-[#94A3B8] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              Why SMBs Choose PriceStrike
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Enterprise-grade pricing intelligence, built for businesses like yours
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-8 text-center hover:border-[#2563EB]/50 transition-colors"
              >
                <div className="w-16 h-16 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-[#2563EB]" />
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">{benefit.title}</h3>
                <p className="text-[#94A3B8]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tool Banner */}
      <section className="py-12 px-6 bg-gradient-to-r from-[#2563EB]/10 via-[#111827] to-[#2563EB]/10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="inline-block bg-[#2563EB]/20 text-[#2563EB] text-xs font-semibold px-3 py-1 rounded-full mb-3">
                FREE TOOL
              </div>
              <h3 className="text-2xl font-bold text-[#F8FAFC] mb-2">
                Try our free Pricing Health Check
              </h3>
              <p className="text-[#94A3B8]">
                Upload your data or use sample products. See your pricing risk score in under 3 minutes.
              </p>
            </div>
            <Link to="/pricing-check" onClick={() => trackCTAClick('Run Free Check - Banner', '/home')}>
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg font-semibold rounded-lg whitespace-nowrap">
                Run Free Check →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              What You Get
            </h2>
            <p className="text-lg text-[#94A3B8]">
              Everything you need to price with confidence
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6 flex gap-5 hover:border-[#2563EB]/50 transition-colors"
              >
                <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-[#2563EB]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">{feature.title}</h3>
                  <p className="text-[#94A3B8] text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 px-6 bg-[#111827]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              Real Problems, Real Solutions
            </h2>
            <p className="text-lg text-[#94A3B8]">
              See how PriceStrike helps businesses like yours
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6"
              >
                <div className="w-12 h-12 bg-[#2563EB]/10 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-[#2563EB]" />
                </div>
                <h3 className="text-lg font-bold text-[#F8FAFC] mb-3">{useCase.title}</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#94A3B8]">{useCase.problem}</p>
                  </div>
                  <div className="flex gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-[#F8FAFC]">{useCase.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
              How It Works
            </h2>
            <p className="text-lg text-[#94A3B8]">
              From upload to insights in 3 simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Upload Your Data", desc: "CSV, Excel, or connect your POS system" },
              { step: "2", title: "Get Your Analysis", desc: "AI identifies safe zones and risk areas" },
              { step: "3", title: "Make Confident Moves", desc: "Act on data-backed recommendations" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-[#2563EB] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-2">{item.title}</h3>
                <p className="text-[#94A3B8]">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/pricing-check" onClick={() => trackCTAClick('Get Started - How It Works', '/home')}>
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-10 py-7 text-lg font-semibold rounded-lg">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C]">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#0A0F1C] border border-[#2563EB]/30 rounded-3xl p-10 md:p-14 text-center">
            <Shield className="w-14 h-14 text-[#2563EB] mx-auto mb-6" />
            
            <h2 className="text-3xl md:text-4xl font-bold text-[#F8FAFC] mb-6">
              Stop Guessing. Start Knowing.
            </h2>
            
            <p className="text-xl text-[#94A3B8] mb-10">
              See where your pricing is safe — before your customers tell you the hard way.
            </p>
            
            <Link to="/pricing-check" onClick={() => trackCTAClick('Run Free Pricing Check - Final CTA', '/home')}>
              <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-12 py-7 text-lg font-semibold rounded-xl shadow-lg shadow-[#2563EB]/25">
                Run Free Pricing Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            
            <p className="text-[#94A3B8] text-sm mt-6">
              No sales call. No credit card. Results in minutes.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      
      <TrialSignupModal 
        isOpen={isTrialModalOpen} 
        onClose={() => setIsTrialModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage;
